import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, Coins, Package } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";
import { credits, rm } from "../../lib/format";
import { Button, Input } from "../ui";
import { CountrySelect } from "../CountrySelect";
import { PhoneInput } from "../PhoneInput";
import { AddressRegionField, formatAddressLocation } from "../AddressRegionField";
import { emptyAddressForm, patchAddressForm } from "../../lib/addressForm";
import { TradeWizard } from "./TradeWizard";
import { StorePickupSection } from "../pickup/StorePickupSection";
import { validatePickupScheduledAt, type PickupOptionsPublic } from "@gaole/shared";

interface AddressDto {
  id: string;
  label: string;
  name: string;
  phone: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
  isDefault: boolean;
}

export interface VaultActionItem {
  id: string;
  name: string;
  estValue: number;
  buybackCredits: number | null;
  canBuyback: boolean;
}

type ActionMode = "choose" | "ship" | "trade" | "buyback";

interface VaultActionsPanelProps {
  selectedItems: VaultActionItem[];
  tradingOn: boolean;
  creditsEnabled: boolean;
  onClear: () => void;
  onTradeCreated: () => void;
  /** Hide bulk selection summary — used in single-item detail modal. */
  compact?: boolean;
  initialMode?: ActionMode;
}

const emptyAddress = emptyAddressForm();

export function VaultActionsPanel({
  selectedItems,
  tradingOn,
  creditsEnabled,
  onClear,
  onTradeCreated,
  compact = false,
  initialMode = "choose",
}: VaultActionsPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedIds = selectedItems.map((i) => i.id);
  const [mode, setMode] = useState<ActionMode>(initialMode);
  const [busy, setBusy] = useState(false);
  const [addressId, setAddressId] = useState<string | null>(null);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddress, setNewAddress] = useState(emptyAddress);
  const [shipMethod, setShipMethod] = useState<"DELIVERY" | "PICKUP">("DELIVERY");
  const [pickupLocationId, setPickupLocationId] = useState("");
  const [pickupScheduledAt, setPickupScheduledAt] = useState("");

  const { data: pickupOptions } = useQuery({
    queryKey: ["pickup-options"],
    queryFn: () => api<PickupOptionsPublic>("/shipping/pickup-options"),
    enabled: mode === "ship",
  });
  const pickupAvailable = Boolean(pickupOptions?.enabled && pickupOptions.locations.length > 0);

  useEffect(() => {
    if (pickupAvailable && !pickupLocationId && pickupOptions?.locations[0]) {
      setPickupLocationId(pickupOptions.locations[0].id);
    }
  }, [pickupAvailable, pickupLocationId, pickupOptions?.locations]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode, selectedIds.join(",")]);

  const totalEstValue = useMemo(
    () => selectedItems.reduce((acc, item) => acc + item.estValue, 0),
    [selectedItems],
  );
  const totalBuybackCredits = useMemo(
    () => selectedItems.reduce((acc, item) => acc + (item.buybackCredits ?? 0), 0),
    [selectedItems],
  );
  const buybackEligible = creditsEnabled && selectedItems.length > 0 && selectedItems.every((i) => i.canBuyback);

  const { data: addresses } = useQuery({
    queryKey: ["addresses"],
    queryFn: () => api<{ items: AddressDto[] }>("/addresses"),
    enabled: mode === "ship",
  });

  useEffect(() => {
    if (showNewAddress) return;
    if (addresses?.items.length && !addressId) {
      const def = addresses.items.find((a) => a.isDefault) ?? addresses.items[0];
      setAddressId(def.id);
    }
    if (addresses && addresses.items.length === 0) setShowNewAddress(true);
  }, [addresses, addressId, showNewAddress]);

  async function pickupSelected() {
    if (!pickupOptions || !pickupLocationId || !pickupScheduledAt) {
      toast("error", "Select a pickup location and time");
      return;
    }
    const location = pickupOptions.locations.find((l) => l.id === pickupLocationId);
    const pickupErr = validatePickupScheduledAt(
      new Date(pickupScheduledAt),
      new Date(),
      pickupOptions.minLeadHours,
      pickupOptions.maxDaysAhead,
      location?.availability,
    );
    if (pickupErr) {
      toast("error", pickupErr);
      return;
    }
    setBusy(true);
    try {
      const res = await api<{ orderId: string; free: boolean }>("/vault/request-shipment", {
        body: { itemIds: selectedIds, pickupLocationId, pickupScheduledAt },
      });
      toast("success", "Pickup requested — we'll prepare your cards");
      onClear();
      await queryClient.invalidateQueries({ queryKey: ["vault"] });
      navigate(`/orders/${res.orderId}`);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't request pickup");
    } finally {
      setBusy(false);
    }
  }

  async function shipSelected() {
    if (shipMethod === "PICKUP") {
      await pickupSelected();
      return;
    }
    setBusy(true);
    try {
      let finalAddressId = addressId ?? undefined;
      if (showNewAddress) {
        if (!newAddress.name || !newAddress.phone || !newAddress.address1 || !newAddress.postcode || !newAddress.city || !newAddress.country || !newAddress.state) {
          toast("error", "Fill in all required address fields");
          setBusy(false);
          return;
        }
        const created = await api<{ address: AddressDto }>("/addresses", {
          body: { ...newAddress, address2: newAddress.address2 || null },
        });
        finalAddressId = created.address.id;
        await queryClient.invalidateQueries({ queryKey: ["addresses"] });
      }
      if (!finalAddressId) {
        toast("error", "Pick or create a shipping address");
        setBusy(false);
        return;
      }
      const res = await api<{ orderId: string; free: boolean }>("/vault/request-shipment", {
        body: { itemIds: selectedIds, addressId: finalAddressId },
      });
      toast("success", res.free ? "Shipment requested — it's on us!" : "Shipment order created — pay the shipping fee to dispatch");
      onClear();
      await queryClient.invalidateQueries({ queryKey: ["vault"] });
      navigate(`/orders/${res.orderId}`);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't request shipment");
    } finally {
      setBusy(false);
    }
  }

  async function buybackSelected() {
    setBusy(true);
    try {
      const res = await api<{ totalCredits: number; itemCount: number }>("/vault/buyback", {
        body: { itemIds: selectedIds },
      });
      toast("success", `${credits(res.totalCredits)} added to your wallet`);
      onClear();
      await queryClient.invalidateQueries({ queryKey: ["vault"] });
      await queryClient.invalidateQueries({ queryKey: ["wallet"] });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't complete buyback");
    } finally {
      setBusy(false);
    }
  }

  if (mode === "trade" && tradingOn) {
    return (
      <TradeWizard
        offerItemIds={selectedIds}
        creditsEnabled={creditsEnabled}
        onClose={() => setMode("choose")}
        onCreated={() => {
          onClear();
          onTradeCreated();
        }}
      />
    );
  }

  const actionCols =
    tradingOn && buybackEligible ? "grid-cols-3" : tradingOn || buybackEligible ? "grid-cols-2" : "";

  return (
    <div className="space-y-2">
      {mode === "choose" && (
        <>
          {!compact && (
            <p className="text-center text-[11px] text-white/50">
              {selectedIds.length} item{selectedIds.length > 1 ? "s" : ""} selected · est. {rm(totalEstValue)}
            </p>
          )}
          <div className={`grid gap-2 ${actionCols}`}>
            <Button className="flex items-center justify-center gap-1.5" onClick={() => setMode("ship")}>
              <Package size={14} />
              Ship
            </Button>
            {tradingOn ? (
              <Button variant="outline" className="flex items-center justify-center gap-1.5" onClick={() => setMode("trade")}>
                <ArrowLeftRight size={14} />
                Trade
              </Button>
            ) : null}
            {buybackEligible ? (
              <Button variant="outline" className="flex items-center justify-center gap-1.5" onClick={() => setMode("buyback")}>
                <Coins size={14} />
                Buyback
              </Button>
            ) : null}
          </div>
          {buybackEligible ? (
            <p className="text-center text-[10px] text-brand-pink">Buyback total: {credits(totalBuybackCredits)}</p>
          ) : null}
          {!compact && (
            <button type="button" onClick={onClear} className="w-full text-center text-[11px] text-white/40">
              Clear selection
            </button>
          )}
        </>
      )}

      {mode === "ship" && (
        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          <div className="flex items-center justify-between">
            <p className="font-display text-[10px] font-semibold uppercase tracking-widest text-white/40">
              {shipMethod === "PICKUP" ? "Pick up at" : "Ship to"}
            </p>
            <button type="button" onClick={() => setMode("choose")} className="text-[11px] text-brand-pink">
              Back
            </button>
          </div>
          {pickupAvailable ? (
            <div className="flex gap-1.5 rounded-full bg-surface p-1">
              {(["DELIVERY", "PICKUP"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setShipMethod(m)}
                  className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                    shipMethod === m ? "bg-gradient-brand text-white" : "text-white/50"
                  }`}
                >
                  {m === "DELIVERY" ? "Delivery" : "Pickup"}
                </button>
              ))}
            </div>
          ) : null}
          <p className="text-[10px] text-white/45">
            Est. value {rm(totalEstValue)} · {shipMethod === "PICKUP" ? "no shipping fee" : "shipping fee at checkout"}
          </p>
          {shipMethod === "PICKUP" && pickupOptions ? (
            <StorePickupSection
              pickupOptions={pickupOptions}
              locationId={pickupLocationId}
              onLocationIdChange={setPickupLocationId}
              scheduledAt={pickupScheduledAt}
              onScheduledAtChange={setPickupScheduledAt}
            />
          ) : null}
          {shipMethod === "DELIVERY" && addresses?.items.map((addr) => (
            <button
              key={addr.id}
              type="button"
              onClick={() => {
                setAddressId(addr.id);
                setShowNewAddress(false);
              }}
              className={`w-full rounded-xl border p-2.5 text-left text-[11px] ${
                addressId === addr.id && !showNewAddress ? "border-brand-pink bg-brand-pink/10" : "border-white/5 bg-surface"
              }`}
            >
              <p className="font-semibold">
                {addr.label} · {addr.name}
              </p>
              <p className="mt-0.5 text-white/55">
                {addr.address1}, {formatAddressLocation(addr)}
              </p>
            </button>
          ))}
          {shipMethod === "PICKUP" ? null : !showNewAddress ? (
            <button
              type="button"
              onClick={() => {
                setShowNewAddress(true);
                setAddressId(null);
              }}
              className="text-[11px] font-medium text-brand-pink"
            >
              + Add new address
            </button>
          ) : (
            <div className="space-y-2 rounded-xl border border-white/10 bg-surface p-2.5">
              <Input placeholder="Recipient name" value={newAddress.name} onChange={(e) => patchAddressForm(setNewAddress, { name: e.target.value })} />
              <PhoneInput
                placeholder="Mobile number"
                value={newAddress.phone}
                onChange={(phone) => patchAddressForm(setNewAddress, { phone })}
                country={newAddress.country}
              />
              <Input placeholder="Address line 1" value={newAddress.address1} onChange={(e) => patchAddressForm(setNewAddress, { address1: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Postcode" value={newAddress.postcode} onChange={(e) => patchAddressForm(setNewAddress, { postcode: e.target.value })} />
                <Input placeholder="City" value={newAddress.city} onChange={(e) => patchAddressForm(setNewAddress, { city: e.target.value })} />
              </div>
              <CountrySelect
                label="Country"
                required
                value={newAddress.country}
                onChange={(country) =>
                  patchAddressForm(setNewAddress, (prev) => ({
                    country,
                    state: country === prev.country ? prev.state : "",
                  }))
                }
              />
              <AddressRegionField
                country={newAddress.country}
                state={newAddress.state}
                onStateChange={(state) => patchAddressForm(setNewAddress, { state })}
              />
            </div>
          )}
          <Button className="w-full" disabled={busy} onClick={shipSelected}>
            {shipMethod === "PICKUP" ? "Pick up" : "Ship"} {selectedIds.length} item{selectedIds.length > 1 ? "s" : ""}
          </Button>
        </div>
      )}

      {mode === "buyback" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="font-display text-[10px] font-semibold uppercase tracking-widest text-white/40">Buyback</p>
            <button type="button" onClick={() => setMode("choose")} className="text-[11px] text-brand-pink">
              Back
            </button>
          </div>
          {!compact && (
            <ul className="max-h-32 space-y-1 overflow-y-auto text-[11px] text-white/70">
              {selectedItems.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-2">
                  <span className="line-clamp-1">{item.name}</span>
                  <span className="shrink-0 text-brand-pink">{credits(item.buybackCredits ?? 0)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-center font-display text-lg font-bold text-brand-pink">{credits(totalBuybackCredits)}</p>
          <p className="text-center text-[10px] text-white/45">Credits are added to your wallet instantly. This cannot be undone.</p>
          <Button className="w-full" disabled={busy} onClick={buybackSelected}>
            Buyback {selectedIds.length} item{selectedIds.length > 1 ? "s" : ""}
          </Button>
        </div>
      )}
    </div>
  );
}
